import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productService, categoryService } from '../services/firestore';
import { Product, Category } from '../types';
import './ProductsPage.css';

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, categoriesData] = await Promise.all([
          productService.getAll(),
          categoryService.getAll()
        ]);
        
        setProducts(productsData);
        setCategories(categoriesData);
        
        // Check if category is selected via URL params
        const categoryParam = searchParams.get('category');
        if (categoryParam) {
          setSelectedCategory(categoryParam);
          
          // Find the category name from the category ID
          const selectedCategoryObj = categoriesData.find(cat => cat.id === categoryParam);
          const categoryName = selectedCategoryObj?.name || '';
          
          if (categoryName) {
            const filteredProducts = await productService.getByCategory(categoryName);
            setProducts(filteredProducts);
          } else {
            setProducts([]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleCategoryChange = async (categoryId: string) => {
    setSelectedCategory(categoryId);
    setLoading(true);
    
    try {
      if (categoryId === '') {
        const allProducts = await productService.getAll();
        setProducts(allProducts);
      } else {
        // Find the category name from the category ID
        const selectedCategoryObj = categories.find(cat => cat.id === categoryId);
        const categoryName = selectedCategoryObj?.name || '';
        
        if (categoryName) {
          const filteredProducts = await productService.getByCategory(categoryName);
          setProducts(filteredProducts);
        } else {
          setProducts([]);
        }
      }
    } catch (error) {
      console.error('Error filtering products:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="container">
        <div className="products-header">
          <h1>Our Products</h1>
          <p>Browse our wide range of custom printing solutions</p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="category-filter">
            <label htmlFor="category-select">Filter by Category:</label>
            <select 
              id="category-select"
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="category-select"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Products Grid */}
        {products.length > 0 ? (
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  <img 
                    src={product.imageUrl || ''} 
                    alt={product.name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  {product.stock <= 0 && (
                    <div className="out-of-stock">Out of Stock</div>
                  )}
                </div>
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-price">
                    {formatPrice(product.price)}
                  </div>
                  <div className="product-actions">
                    <Link 
                      to={`/product/${product.id}`}
                      className="btn btn-primary"
                    >
                      View Product
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-products">
            <h3>No products found</h3>
            <p>Try selecting a different category or check back later for new products.</p>
            <Link to="/custom-order" className="btn btn-outline">
              Request Custom Order
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
